import { useCallback, useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import ConfirmationWrapper from "../common/ConfirmationWrapper";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import PageTitle from "../common/PageTitle";
import ProgressiveControl from "../common/ProgressiveControl";
import TabHeader from "../common/TabHeader";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { TEXT_NORMAL, TEXT_SMALL } from "../../style";
import WebsiteMediaInput from "./WebsiteMediaInput";

export default function WebsiteGalleryAlbums() {
    const { requestAPI, showToast } = useAppContext();
    const [loading, setLoading] = useState();
    const [error, setError] = useState();
    const [rows, setRows] = useState();
    const [dialog, setDialog] = useState({ visible: false });

    const loadRows = useCallback(() => {
        requestAPI({
            requestPath: "website/gallery-albums",
            requestMethod: "GET",
            setLoading,
            onRequestStart: setError,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) setRows(data);
                else setError("Couldn't load Gallery Items");
            },
        });
    }, [requestAPI]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    return (
        <div className="flex flex-column h-full">
            <PageTitle title="Gallery Items" />
            <TabHeader
                className="px-3 pt-3"
                title="Gallery Items"
                highlights={["Add, edit, and delete rows"]}
                actionItems={[
                    <Button icon="pi pi-plus" severity="warning" onClick={() => setDialog({ visible: true, record: {} })} />,
                ]}
            />
            <Divider />
            {loading ? <Loading /> : error ? <NoContent error={error} /> : (
                <div className="flex-1 overflow-y-scroll flex flex-column gap-2 p-2">
                    {rows?.length ? rows.map((row) => (
                        <Row key={row.id} row={row} setRows={setRows} requestAPI={requestAPI} showToast={showToast} openEdit={(record) => setDialog({ visible: true, record })} />
                    )) : <NoContent />}
                </div>
            )}
            {dialog.visible ? <Editor dialog={dialog} setDialog={setDialog} setRows={setRows} requestAPI={requestAPI} showToast={showToast} /> : null}
        </div>
    );
}

function Row({ row, setRows, requestAPI, showToast, openEdit }) {
    const [deleting, setDeleting] = useState();
    const remove = () => {
        requestAPI({
            requestPath: `website/gallery-albums/${row.id}`,
            requestMethod: "DELETE",
            setLoading: setDeleting,
            parseResponseBody: false,
            onResponseReceieved: (_, responseCode) => {
                if (responseCode === 204) {
                    showToast({ severity: "success", summary: "Deleted", detail: "Gallery Items Deleted", life: 1000 });
                    setRows((prev) => prev?.filter((item) => item.id !== row.id));
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: "Failed To Delete !", life: 2000 });
                }
            },
        });
    };
    const active = row.is_published;
    return (
        <div className={`flex gap-3 align-items-center border-round py-2 px-3 text-white ${active ? "bg-green-500" : "bg-red-500"}`}>
            <div className="flex flex-column flex-1 overflow-hidden">
                <span className={`${TEXT_NORMAL} font-semibold`}>{row.id}. {row.title || "Untitled"}</span>
            </div>
            <i className="pi pi-pencil cursor-pointer" onClick={() => openEdit(row)} />
            <ProgressiveControl loading={deleting} control={<ConfirmationWrapper action={remove}><i className="pi pi-trash cursor-pointer" /></ConfirmationWrapper>} />
        </div>
    );
}

function Editor({ dialog, setDialog, setRows, requestAPI, showToast }) {
    const [record, setRecord] = useState({ is_active: true, is_published: true, ...(dialog.record || {}) });
    const [saving, setSaving] = useState();
    const isNew = !record.id;
    const save = () => {
        requestAPI({
            requestPath: "website/gallery-albums",
            requestMethod: isNew ? "POST" : "PATCH",
            requestPostBody: record,
            setLoading: setSaving,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Save !", life: 2000 }),
            onResponseReceieved: (saved, responseCode) => {
                if (saved && (responseCode === 200 || responseCode === 201)) {
                    setRows((prev) => (isNew ? [...(prev || []), saved] : prev.map((item) => (item.id === saved.id ? saved : item))));
                    showToast({ severity: "success", summary: "Saved", detail: "Gallery Items Saved", life: 1000 });
                    setDialog({ visible: false });
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: saved?.error || "Failed To Save !", life: 2000 });
                }
            },
        });
    };
    return (
        <Dialog header={isNew ? "Add" : "Edit"} visible className="w-11" onHide={() => setDialog({ visible: false })}>
            <div className="flex flex-column">
                    <FloatLabel className="mt-5">
                        <InputText id="category_slug" className="w-full" value={record?.category_slug ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, category_slug: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="category_slug">Category Slug</label>
                    </FloatLabel>
                    <FloatLabel className="mt-5">
                        <InputText id="title" className="w-full" value={record?.title ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, title: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="title">Title</label>
                    </FloatLabel>
                    <FloatLabel className="mt-5">
                        <InputTextarea id="description" className="w-full" rows={4} autoResize value={record?.description || ""} onChange={(event) => setRecord((prev) => ({ ...prev, description: event.target.value }))} disabled={saving} />
                        <label htmlFor="description">Description</label>
                    </FloatLabel>
                    <FloatLabel className="mt-5">
                        <InputText id="location" className="w-full" value={record?.location ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, location: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="location">Location</label>
                    </FloatLabel>
                    <FloatLabel className="mt-5">
                        <InputText id="event_date" className="w-full" value={record?.event_date ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, event_date: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="event_date">Event Date</label>
                    </FloatLabel>
                    <WebsiteMediaInput label="Cover image" field="cover_image" hint="Required size: 1280 ? 800 px (16:10). A larger photo is scaled to fit this event card. The whole photo stays visible." record={record} setRecord={setRecord} disabled={saving} />
                    <FloatLabel className="mt-5">
                        <InputText id="cover_alt" className="w-full" value={record?.cover_alt ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, cover_alt: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="cover_alt">Cover Alt</label>
                    </FloatLabel>
                    <div className="flex align-items-center gap-2 mt-4">
                        <Checkbox inputId="is_featured" checked={!!record?.is_featured} onChange={(event) => setRecord((prev) => ({ ...prev, is_featured: event.checked }))} disabled={saving} />
                        <label htmlFor="is_featured" className={TEXT_SMALL}>Is Featured</label>
                    </div>
                    <div className="flex align-items-center gap-2 mt-4">
                        <Checkbox inputId="is_published" checked={!!record?.is_published} onChange={(event) => setRecord((prev) => ({ ...prev, is_published: event.checked }))} disabled={saving} />
                        <label htmlFor="is_published" className={TEXT_SMALL}>Is Published</label>
                    </div>
                    <FloatLabel className="mt-5">
                        <InputText id="tags" className="w-full" value={record?.tags ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, tags: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="tags">Tags</label>
                    </FloatLabel>
                    <FloatLabel className="mt-5">
                        <InputText id="item_type" className="w-full" value={record?.item_type ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, item_type: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="item_type">Item Type</label>
                    </FloatLabel>
                    <FloatLabel className="mt-5">
                        <InputText id="person_name" className="w-full" value={record?.person_name ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, person_name: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="person_name">Person Name</label>
                    </FloatLabel>
                    <FloatLabel className="mt-5">
                        <InputText id="designation" className="w-full" value={record?.designation ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, designation: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="designation">Designation</label>
                    </FloatLabel>
                    <FloatLabel className="mt-5">
                        <InputText id="department" className="w-full" value={record?.department ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, department: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="department">Department</label>
                    </FloatLabel>
                    <FloatLabel className="mt-5">
                        <InputText id="experience" className="w-full" value={record?.experience ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, experience: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="experience">Experience</label>
                    </FloatLabel>
                    <WebsiteMediaInput label="Portrait" field="person_image" hint="Required size: 800 ? 1000 px (4:5). A larger photo is scaled to fit this people card. The whole photo stays visible." record={record} setRecord={setRecord} disabled={saving} />
                    <FloatLabel className="mt-5">
                        <InputText id="person_image_alt" className="w-full" value={record?.person_image_alt ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, person_image_alt: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="person_image_alt">Person Image Alt</label>
                    </FloatLabel>
                <Button className="mt-5 align-self-start" label="Save" icon="pi pi-check" loading={saving} onClick={save} />
            </div>
        </Dialog>
    );
}
